import React, { useContext, useEffect, useReducer, useState, useMemo } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import useHelps from "../hooks/useHelps";
import ListItem from "@material-ui/core/ListItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";
import Divider from "@material-ui/core/Divider";
import Badge from "@material-ui/core/Badge";
import Collapse from "@material-ui/core/Collapse";
import List from "@material-ui/core/List";
import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";
import Box from "@material-ui/core/Box";
import Avatar from "@material-ui/core/Avatar";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import PersonIcon from "@mui/icons-material/Person";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import StarIcon from "@mui/icons-material/Star";

// Todos os ícones necessários
import AssessmentIcon from '@mui/icons-material/Assessment';
import EqualizerIcon from '@mui/icons-material/Equalizer';
import DashboardIcon from "@mui/icons-material/Dashboard";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import SyncAltIcon from "@mui/icons-material/SyncAlt";
import SettingsIcon from "@mui/icons-material/Settings";
import PeopleIcon from "@mui/icons-material/People";
import ContactPhoneIcon from "@mui/icons-material/ContactPhone";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import FlashOnIcon from "@mui/icons-material/FlashOn";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import CodeIcon from "@mui/icons-material/Code";
import ViewKanbanIcon from "@mui/icons-material/ViewKanban";
import ScheduleIcon from "@mui/icons-material/Schedule";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ListAltIcon from "@mui/icons-material/ListAlt";
import AnnouncementIcon from "@mui/icons-material/Announcement";
import ForumIcon from "@mui/icons-material/Forum";
import LocalAtmIcon from "@mui/icons-material/LocalAtm";
import BusinessIcon from "@mui/icons-material/Business";
import AllInclusiveIcon from "@mui/icons-material/AllInclusive";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import DeviceHubIcon from "@mui/icons-material/DeviceHub";
import CampaignIcon from "@mui/icons-material/Campaign";
import ShapeLineIcon from "@mui/icons-material/ShapeLine";
import WebhookIcon from "@mui/icons-material/Webhook";
import ChatBubbleIcon from "@mui/icons-material/ChatBubble";
import BuildIcon from "@mui/icons-material/Build";

import { WhatsAppsContext } from "../context/WhatsApp/WhatsAppsContext";
import { AuthContext } from "../context/Auth/AuthContext";
import { useActiveMenu } from "../context/ActiveMenuContext";
import { Can } from "../components/Can";
import api from "../services/api";
import toastError from "../errors/toastError";
import usePlans from "../hooks/usePlans";
import useVersion from "../hooks/useVersion";
import { i18n } from "../translate/i18n";
import moment from "moment";

const useStyles = makeStyles((theme) => ({
  menuWrapper: {
    backgroundColor: theme.palette.type === "light" ? "#ffffff" : theme.palette.background.paper,
    height: "100vh",
    display: "flex",
    flexDirection: "column",
    borderRight: `1px solid ${theme.palette.divider}`,
    boxShadow: "0 2px 20px rgba(0, 0, 0, 0.03)",
    position: "relative",
    overflow: "hidden",
  },
  menuHeader: {
    padding: "24px 20px 16px",
    borderBottom: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.type === "light" ? "#ffffff" : theme.palette.background.paper,
  },

  userInfo: {
    display: "flex",
    alignItems: "center",
    marginBottom: "20px",
    cursor: "pointer",
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: "rgba(101, 23, 171, 0.03)",
      borderRadius: "8px",
      padding: "4px",
    },
  },
  userAvatar: {
    width: 40,
    height: 40,
    backgroundColor: "rgba(101, 23, 171, 0.12)",
    color: "#6517ab",
    fontWeight: "600",
    fontSize: "16px",
    marginRight: "12px",
    border: "2px solid rgba(101, 23, 171, 0.2)",
  },
  userDetails: {
    flex: 1,
  },
  userName: {
    fontSize: "16px",
    fontWeight: "600",
    color: theme.palette.text.primary,
    lineHeight: "1.3",
  },

  userRole: {
    fontSize: "13px",
    color: theme.palette.text.secondary,
    fontWeight: "400",
  },

  searchBar: {
    position: "relative",
    marginTop: "16px",
  },
  searchInput: {
    width: "100%",
    padding: "12px 16px 12px 42px",
    borderRadius: "14px",
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.type === 'light' ? "rgba(101, 23, 171, 0.04)" : "rgba(255, 255, 255, 0.05)",
    fontSize: "14px",
    color: theme.palette.text.primary,
    outline: "none",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",

    "&:focus": {
      borderColor: theme.palette.primary.main,
      backgroundColor: theme.palette.background.paper,
      boxShadow: `0 0 0 4px ${theme.palette.primary.main}15`,
    },
    "&::placeholder": {
      color: theme.palette.text.secondary,
      opacity: 0.6,
    },
  },
  searchIcon: {
    position: "absolute",
    left: "12px",
    top: "50%",
    transform: "translateY(-50%)",
    color: "#6517ab",
    fontSize: "18px",
  },
  clearIcon: {
    position: "absolute",
    right: "12px",
    top: "50%",
    transform: "translateY(-50%)",
    color: "#999",
    fontSize: "18px",
    cursor: "pointer",
    "&:hover": {
      color: "#6517ab",
    },
  },
  menuContent: {
    flex: 1,
    overflowY: "auto",
    padding: "16px 0",
    scrollbarWidth: "thin",
    scrollbarColor: "rgba(101, 23, 171, 0.3) transparent",
    "&::-webkit-scrollbar": {
      width: "6px",
    },
    "&::-webkit-scrollbar-track": {
      background: "transparent",
    },
    "&::-webkit-scrollbar-thumb": {
      background: "rgba(101, 23, 171, 0.2)",
      borderRadius: "3px",
      "&:hover": {
        background: "rgba(101, 23, 171, 0.3)",
      },
    },
  },
  menuSection: {
    marginBottom: "16px",
    padding: "0 12px",
  },
  sectionTitle: {
    fontSize: "11px",
    fontWeight: "700",
    color: theme.palette.text.secondary,
    textTransform: "uppercase",
    letterSpacing: "1.2px",
    padding: "0 16px 12px",
    opacity: 0.6,
  },
  menuItem: {
    minHeight: "48px",
    padding: "10px 16px",
    margin: "4px 0",
    borderRadius: "12px",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    position: "relative",
    "&:hover": {
      backgroundColor: theme.palette.type === 'light' ? "rgba(101, 23, 171, 0.05)" : "rgba(255, 255, 255, 0.05)",
      transform: "translateX(4px)",
    },
    "&.active": {
      backgroundColor: theme.palette.type === 'light' ? "rgba(101, 23, 171, 0.1)" : "rgba(101, 23, 171, 0.2)",
      "& .menu-item-text": {
        color: theme.palette.primary.main,
        fontWeight: "700",
      },
      "& .menu-item-icon": {
        color: theme.palette.primary.main,
        transform: "scale(1.1)",
      },
      "&::before": {
        content: '""',
        position: "absolute",
        left: "-12px",
        top: "15%",
        height: "70%",
        width: "4px",
        backgroundColor: theme.palette.primary.main,
        borderRadius: "0 4px 4px 0",
      }
    },
  },
  menuItemIcon: {
    minWidth: "40px",
    color: theme.palette.text.secondary,
    transition: "all 0.3s ease",

    "& .MuiSvgIcon-root": {
      fontSize: "22px",
    },
  },
  menuItemText: {
    fontSize: "14px",
    fontWeight: "500",
    color: theme.palette.text.primary,
    transition: "all 0.3s ease",
  },

  subMenuItem: {
    minHeight: "40px",
    padding: "8px 16px 8px 48px",
    margin: "2px 0",
    borderRadius: "10px",
    transition: "all 0.3s ease",
    "&:hover": {
      backgroundColor: "rgba(101, 23, 171, 0.03)",
      transform: "translateX(4px)",
    },
    "&.active": {
      backgroundColor: "rgba(101, 23, 171, 0.06)",
      "& .submenu-item-text": {
        color: theme.palette.primary.main,
        fontWeight: "600",
      },
    },
  },
  subMenuItemText: {
    fontSize: "13.5px",
    fontWeight: "450",
    color: theme.palette.text.secondary,
    transition: "all 0.3s ease",
  },

  badge: {
    "& .MuiBadge-badge": {
      backgroundColor: "#ff4757",
      color: "#ffffff",
      fontSize: "10px",
      fontWeight: "bold",
      minWidth: "16px",
      height: "16px",
      borderRadius: "8px",
      border: "2px solid #ffffff",
    },
  },
  menuFooter: {
    padding: "16px 20px",
    borderTop: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.type === "light" ? "#ffffff" : theme.palette.background.paper,
  },

  footerItem: {
    padding: "12px 16px",
    display: "flex",
    alignItems: "center",
    cursor: "pointer",
    transition: "all 0.2s ease",
    borderRadius: "8px",
    "&:hover": {
      backgroundColor: "rgba(101, 23, 171, 0.05)",
      "& .footer-item-text": {
        color: "#6517ab",
      },
      "& .footer-item-icon": {
        color: "#6517ab",
      },
    },
  },
  footerItemText: {
    fontSize: "14px",
    fontWeight: "500",
    color: theme.palette.text.secondary,
    transition: "color 0.2s ease",

    marginLeft: "12px",
    flex: 1,
  },
  footerItemIcon: {
    color: "#999",
    fontSize: "20px",
    transition: "color 0.2s ease",
  },
  collapsedMenu: {
    width: "80px",
    alignItems: "center",
    "& $menuHeader": {
      padding: "20px 12px",
      textAlign: "center",
    },
    "& $userInfo": {
      flexDirection: "column",
      alignItems: "center",
      textAlign: "center",
      marginBottom: "16px",
    },
    "& $userAvatar": {
      marginRight: 0,
      marginBottom: "8px",
      width: 44,
      height: 44,
    },
    "& $userDetails": {
      display: "none",
    },
    "& $searchBar": {
      display: "none",
    },
    "& $sectionTitle": {
      display: "none",
    },
    "& $menuSection": {
      padding: "0 8px",
    },
    "& $menuItem": {
      padding: "12px",
      justifyContent: "center",
      margin: "8px 0",
      borderRadius: "14px",
      transform: "none",
      "&:hover": {
        backgroundColor: "rgba(101, 23, 171, 0.1)",
        transform: "scale(1.05)",
      },
      "&.active": {
        backgroundColor: "rgba(101, 23, 171, 0.15)",
        "&::before": {
          display: "none",
        }
      },
    },
    "& $menuItemIcon": {
      minWidth: "auto",
      marginRight: 0,
    },
    "& $menuItemText": {
      display: "none",
    },
    "& $subMenuItem": {
      padding: "10px",
      justifyContent: "center",
      margin: "4px 0",
      borderRadius: "10px",
      "&:hover": {
        backgroundColor: "rgba(101, 23, 171, 0.08)",
      },
    },
    "& $subMenuItemText": {
      display: "none",
    },
    "& $menuFooter": {
      padding: "16px 8px",
      textAlign: "center",
    },
    "& $footerItem": {
      justifyContent: "center",
      padding: "12px",
      borderRadius: "12px",
    },
    "& $footerItemText": {
      display: "none",
    },
  },
  userMenu: {
    "& .MuiPaper-root": {
      borderRadius: "8px",
      boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
      border: "1px solid rgba(101, 23, 171, 0.1)",
      marginTop: "8px",
    },
    "& .MuiMenuItem-root": {
      fontSize: "14px",
      padding: "10px 16px",
      minHeight: "40px",
      "&:hover": {
        backgroundColor: "rgba(101, 23, 171, 0.05)",
      },
    },
  },
  versionText: {
    fontSize: "11px",
    color: theme.palette.text.secondary,
    textAlign: "center",
    padding: "8px 16px",
    borderTop: `1px solid ${theme.palette.divider}`,
    marginTop: "8px",
  },

  expandIcon: {
    fontSize: "18px",
    color: "#999",
  },
  submenuIcon: {
    fontSize: "16px",
    color: "#6517ab",
    marginRight: "8px",
  },
  searchResult: {
    backgroundColor: "rgba(101, 23, 171, 0.05)",
    borderLeft: "3px solid #6517ab",
  },
  noResults: {
    padding: "20px",
    textAlign: "center",
    color: "#888",
    fontSize: "14px",
  },
}));

function MenuItemLink({ icon, primary, to, badge, collapsed, onClick, isSubItem = false, highlight = false }) {
  const classes = useStyles();
  const { activeMenu } = useActiveMenu();
  const location = useLocation();
  const { user } = useContext(AuthContext);
  const isActive = activeMenu === to || location.pathname === to;

  const renderLink = React.useMemo(
    () => React.forwardRef((itemProps, ref) => <RouterLink to={to} ref={ref} {...itemProps} />),
    [to]
  );

  const itemClass = isSubItem ? classes.subMenuItem : classes.menuItem;
  const textClass = isSubItem ? classes.subMenuItemText : classes.menuItemText;

  const itemContent = (
    <ListItem
      button
      component={onClick ? undefined : renderLink}
      onClick={onClick}
      className={`${itemClass} ${isActive ? "active" : ""} ${highlight ? classes.searchResult : ""}`}
    >
      <ListItemIcon className={classes.menuItemIcon}>
        {badge ? (
          <Badge badgeContent={badge} color="error" className={classes.badge}>
            <Box className="menu-item-icon">{icon}</Box>
          </Badge>
        ) : (
          <Box className="menu-item-icon">{icon}</Box>
        )}
      </ListItemIcon>
      {!collapsed && (
        <ListItemText
          primary={
            <Typography className={textClass}>
              {primary}
            </Typography>
          }
        />
      )}
    </ListItem>
  );

  if (collapsed && !onClick) {
    return (
      <Tooltip title={primary} placement="right" arrow>
        {itemContent}
      </Tooltip>
    );
  }

  return itemContent;
}

const reducer = (state, action) => {
  switch (action.type) {
    case "LOAD_CHATS":
      return [...state, ...action.payload];
    case "UPDATE_CHATS":
      return state.map((chat) => (chat.id === action.payload.id ? action.payload : chat));
    case "DELETE_CHAT":
      return state.filter((chat) => chat.id !== action.payload);
    case "RESET":
      return [];
    case "CHANGE_CHAT":
      return state.map((chat) => (chat.id === action.payload.chat.id ? action.payload.chat : chat));
    default:
      return state;
  }
};

// Função para verificar permissões do usuário
const checkUserPermission = (user, itemAllowedProfiles) => {
  if (!user || !user.profile) return false;

  // Se o item não especificar perfis permitidos, permitir para todos
  if (!itemAllowedProfiles || itemAllowedProfiles.length === 0) {
    return true;
  }

  return itemAllowedProfiles.includes(user.profile);
};

// Definição dos itens do menu com permissões específicas
export const menuItemsData = [
  // Comunicação - TODOS OS PERFIS
  {
    type: 'section',
    title: 'Comunicação',
    icon: ChatBubbleIcon,
    allowedProfiles: ['admin', 'supervisor', 'user']
  },
  {
    type: 'item',
    title: 'Tickets',
    to: '/tickets',
    icon: WhatsAppIcon,
    section: 'Comunicação',
    allowedProfiles: ['admin', 'supervisor', 'user']
  },
  {
    type: 'item',
    title: 'Mensagens Rápidas',
    to: '/quick-messages',
    icon: FlashOnIcon,
    section: 'Comunicação',
    allowedProfiles: ['admin', 'supervisor', 'user']
  },
  {
    type: 'item',
    title: 'Contatos',
    to: '/contacts',
    icon: ContactPhoneIcon,
    section: 'Comunicação',
    allowedProfiles: ['admin', 'supervisor', 'user']
  },
  {
    type: 'item',
    title: 'Agendamentos',
    to: '/schedules',
    icon: ScheduleIcon,
    section: 'Comunicação',
    allowedProfiles: ['admin', 'supervisor', 'user']
  },
  {
    type: 'item',
    title: 'Tags',
    to: '/tags',
    icon: LocalOfferIcon,
    section: 'Comunicação',
    allowedProfiles: ['admin', 'supervisor', 'user']
  },
  {
    type: 'item',
    title: 'Chats Internos',
    to: '/chats',
    icon: ForumIcon,
    section: 'Comunicação',
    allowedProfiles: ['admin', 'supervisor', 'user']
  },

  // Gerenciamento - APENAS ADMIN e SUPERVISOR
  {
    type: 'section',
    title: 'Gerenciamento',
    icon: DashboardIcon,
    allowedProfiles: ['admin', 'supervisor']
  },
  {
    type: 'item',
    title: 'Dashboard',
    to: '/',
    icon: AssessmentIcon,
    section: 'Gerenciamento',
    allowedProfiles: ['admin', 'supervisor']
  },
  {
    type: 'item',
    title: 'Central de Relatórios',
    to: '/reports-central',
    icon: EqualizerIcon,
    section: 'Gerenciamento',
    allowedProfiles: ['admin', 'supervisor']
  },
  {
    type: 'item',
    title: 'Relatório de Receitas',
    to: '/revenue-report',
    icon: LocalAtmIcon,
    section: 'Gerenciamento',
    allowedProfiles: ['admin', 'supervisor']
  },
  {
    type: 'item',
    title: 'Relatórios',
    to: '/reports',
    icon: ListAltIcon,
    section: 'Gerenciamento',
    allowedProfiles: ['admin', 'supervisor']
  },
  {
    type: 'item',
    title: 'Relatório de Tags',
    to: '/tags-report',
    icon: LocalOfferIcon,
    section: 'Gerenciamento',
    allowedProfiles: ['admin', 'supervisor']
  },
  {
    type: 'item',
    title: 'Relatório de Usuários',
    to: '/users-report',
    icon: PeopleIcon,
    section: 'Gerenciamento',
    allowedProfiles: ['admin', 'supervisor']
  },
  {
    type: 'item',
    title: 'Relatório de Avaliações',
    to: '/ratings-report',
    icon: <StarIcon />,
    section: 'Gerenciamento',
    allowedProfiles: ['admin', 'supervisor']
  },
  {
    type: 'item',
    title: 'Relatório de Pausas',
    to: '/pauses/report',
    icon: ListAltIcon,
    section: 'Gerenciamento',
    allowedProfiles: ['admin', 'supervisor']
  },
  {
    type: 'item',
    title: 'Chats Tempo Real',
    to: '/moments',
    icon: ForumIcon,
    section: 'Gerenciamento',
    allowedProfiles: ['admin', 'supervisor']
  },

  // Campanhas - APENAS ADMIN e SUPERVISOR
  {
    type: 'section',
    title: 'Campanhas',
    icon: CampaignIcon,
    allowedProfiles: ['admin', 'supervisor']
  },
  {
    type: 'item',
    title: 'Envio Rápido',
    to: '/campaign-wizard',
    icon: FlashOnIcon,
    section: 'Campanhas',
    allowedProfiles: ['admin', 'supervisor']
  },
  {
    type: 'item',
    title: 'Lista de Campanhas',
    to: '/campaigns',
    icon: ListAltIcon,
    section: 'Campanhas',
    allowedProfiles: ['admin', 'supervisor']
  },
  {
    type: 'item',
    title: 'Lista de Contatos',
    to: '/contact-lists',
    icon: PeopleIcon,
    section: 'Campanhas',
    allowedProfiles: ['admin', 'supervisor']
  },
  {
    type: 'item',
    title: 'Configurações',
    to: '/campaigns-config',
    icon: SettingsIcon,
    section: 'Campanhas',
    allowedProfiles: ['admin', 'supervisor']
  },

  // Fluxos - APENAS ADMIN e SUPERVISOR
  {
    type: 'section',
    title: 'Fluxos',
    icon: WebhookIcon,
    allowedProfiles: ['admin', 'supervisor']
  },
  {
    type: 'item',
    title: 'Fluxo de Campanha',
    to: '/phrase-lists',
    icon: CampaignIcon,
    section: 'Fluxos',
    allowedProfiles: ['admin', 'supervisor']
  },
  {
    type: 'item',
    title: 'Fluxo de conversa',
    to: '/flowbuilders',
    icon: ShapeLineIcon,
    section: 'Fluxos',
    allowedProfiles: ['admin', 'supervisor']
  },

  // Ferramentas - TODOS OS PERFIS
  {
    type: 'section',
    title: 'Ferramentas',
    icon: BuildIcon,
    allowedProfiles: ['admin', 'supervisor', 'user']
  },
  {
    type: 'item',
    title: 'Kanban',
    to: '/kanban',
    icon: ViewKanbanIcon,
    section: 'Ferramentas',
    allowedProfiles: ['admin', 'supervisor', 'user']
  },
  {
    type: 'item',
    title: 'Tarefas',
    to: '/todolist',
    icon: EventAvailableIcon,
    section: 'Ferramentas',
    allowedProfiles: ['admin', 'supervisor', 'user']
  },
  {
    type: 'item',
    title: 'Ajuda',
    to: '/helps',
    icon: HelpOutlineIcon,
    section: 'Ferramentas',
    allowedProfiles: ['admin', 'supervisor', 'user']
  },
  {
    type: 'item',
    title: 'Documentação',
    to: '/documentacao',
    icon: CodeIcon,
    section: 'Ferramentas',
    allowedProfiles: ['admin', 'supervisor', 'user']
  },

  // Administração - APENAS ADMIN
  {
    type: 'section',
    title: 'Administração',
    icon: SettingsIcon,
    allowedProfiles: ['admin']
  },
  {
    type: 'item',
    title: 'Anúncios',
    to: '/announcements',
    icon: AnnouncementIcon,
    section: 'Administração',
    allowedProfiles: ['admin']
  },
  {
    type: 'item',
    title: 'Messages API',
    to: '/messages-api',
    icon: CodeIcon,
    section: 'Administração',
    allowedProfiles: ['admin']
  },
  {
    type: 'item',
    title: 'Usuários',
    to: '/users',
    icon: PeopleIcon,
    section: 'Administração',
    allowedProfiles: ['admin']
  },
  {
    type: 'item',
    title: 'Filas',
    to: '/queues',
    icon: AccountTreeIcon,
    section: 'Administração',
    allowedProfiles: ['admin']
  },
  {
    type: 'item',
    title: 'Pausas',
    to: '/pauses',
    icon: DashboardIcon,
    section: 'Administração',
    allowedProfiles: ['admin']
  },
  {
    type: 'item',
    title: 'Prompts',
    to: '/prompts',
    icon: AllInclusiveIcon,
    section: 'Administração',
    allowedProfiles: ['admin']
  },
  {
    type: 'item',
    title: 'Integração de Filas',
    to: '/queue-integration',
    icon: DeviceHubIcon,
    section: 'Administração',
    allowedProfiles: ['admin']
  },
  {
    type: 'item',
    title: 'Conexões',
    to: '/connections',
    icon: SyncAltIcon,
    section: 'Administração',
    allowedProfiles: ['admin']
  },
  {
    type: 'item',
    title: 'Todas as Conexões',
    to: '/allConnections',
    icon: SyncAltIcon,
    section: 'Administração',
    allowedProfiles: ['admin']
  },
  {
    type: 'item',
    title: 'Arquivos',
    to: '/files',
    icon: AttachFileIcon,
    section: 'Administração',
    allowedProfiles: ['admin']
  },
  {
    type: 'item',
    title: 'Financeiro',
    to: '/financeiro',
    icon: LocalAtmIcon,
    section: 'Administração',
    allowedProfiles: ['admin']
  },
  {
    type: 'item',
    title: 'Configurações',
    to: '/settings',
    icon: SettingsIcon,
    section: 'Administração',
    allowedProfiles: ['admin']
  },
  {
    type: 'item',
    title: 'Empresas',
    to: '/companies',
    icon: BusinessIcon,
    section: 'Administração',
    allowedProfiles: ['admin']
  },
];

const MainListItems = ({ collapsed = false, drawerClose }) => {
  const classes = useStyles();
  const { whatsApps } = useContext(WhatsAppsContext);
  const { user, socket, handleLogout } = useContext(AuthContext);
  const { setActiveMenu } = useActiveMenu();
  const location = useLocation();

  const [connectionWarning, setConnectionWarning] = useState(false);
  const [openManagementSubmenu, setOpenManagementSubmenu] = useState(false);
  const [openCommunicationSubmenu, setOpenCommunicationSubmenu] = useState(false);
  const [openCampaignSubmenu, setOpenCampaignSubmenu] = useState(false);
  const [openFlowSubmenu, setOpenFlowSubmenu] = useState(false);
  const [openAdministrationSubmenu, setOpenAdministrationSubmenu] = useState(false);
  const [openIntegrationSubmenu, setOpenIntegrationSubmenu] = useState(false);
  const [showCampaigns, setShowCampaigns] = useState(false);
  const [showKanban, setShowKanban] = useState(false);
  const [planExpired, setPlanExpired] = useState(false);
  const [showOpenAi, setShowOpenAi] = useState(false);
  const [showIntegrations, setShowIntegrations] = useState(false);
  const [showSchedules, setShowSchedules] = useState(false);
  const [showInternalChat, setShowInternalChat] = useState(false);
  const [showExternalApi, setShowExternalApi] = useState(false);
  const [invisible, setInvisible] = useState(true);
  const [pageNumber, setPageNumber] = useState(1);
  const [searchParam, setSearchParam] = useState("");
  const [chats, dispatch] = useReducer(reducer, []);
  const [unreadCount, setUnreadCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  const { list } = useHelps();
  const [hasHelps, setHasHelps] = useState(false);
  const [filteredSections, setFilteredSections] = useState([]);

  const { getPlanCompany } = usePlans();
  const { getVersion } = useVersion();
  const [version, setVersion] = useState("");

  // Função auxiliar para verificar permissões
  const hasPermission = (allowedProfiles) => {
    if (!user || !user.profile) return false;
    if (!allowedProfiles || allowedProfiles.length === 0) return true;
    return allowedProfiles.includes(user.profile);
  };

  // Efeito para abrir submenus baseado na rota atual
  useEffect(() => {
    if (location.pathname === "/" ||
      location.pathname.startsWith("/reports") ||
      location.pathname.startsWith("/moments")) {
      setOpenManagementSubmenu(true);
    }

    if (location.pathname.startsWith("/tickets") ||
      location.pathname.startsWith("/quick-messages") ||
      location.pathname.startsWith("/contacts") ||
      location.pathname.startsWith("/schedules") ||
      location.pathname.startsWith("/tags") ||
      location.pathname.startsWith("/chats")) {
      setOpenCommunicationSubmenu(true);
    }

    if (location.pathname.startsWith("/campaigns") ||
      location.pathname.startsWith("/contact-lists") ||
      location.pathname.startsWith("/campaigns-config")) {
      setOpenCampaignSubmenu(true);
    }

    if (location.pathname.startsWith("/phrase-lists") ||
      location.pathname.startsWith("/flowbuilders")) {
      setOpenFlowSubmenu(true);
    }

    if (location.pathname.startsWith("/users") ||
      location.pathname.startsWith("/queues") ||
      location.pathname.startsWith("/prompts") ||
      location.pathname.startsWith("/queue-integration") ||
      location.pathname.startsWith("/connections") ||
      location.pathname.startsWith("/allConnections") ||
      location.pathname.startsWith("/files") ||
      location.pathname.startsWith("/financeiro") ||
      location.pathname.startsWith("/settings") ||
      location.pathname.startsWith("/companies")) {
      setOpenAdministrationSubmenu(true);
    }

    if (location.pathname.startsWith("/messages-api") ||
      location.pathname.startsWith("/kanban")) {
      setOpenIntegrationSubmenu(true);
    }
  }, [location.pathname]);

  // Efeito para filtrar itens do menu quando searchQuery muda
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredSections([]);
      return;
    }

    const query = searchQuery.toLowerCase().trim();
    const filtered = [];
    const foundItems = [];

    // Filtrar itens que correspondem à busca
    menuItemsData.forEach(item => {
      if (item.type === 'item' &&
        item.title.toLowerCase().includes(query) &&
        hasPermission(item.allowedProfiles)) {
        foundItems.push(item);
      }
    });

    // Agrupar itens por seção
    const sectionsMap = new Map();
    foundItems.forEach(item => {
      if (!sectionsMap.has(item.section)) {
        sectionsMap.set(item.section, []);
      }
      sectionsMap.get(item.section).push(item);
    });

    // Criar estrutura de seções filtradas
    sectionsMap.forEach((items, sectionName) => {
      // Encontrar a seção correspondente
      const sectionData = menuItemsData.find(s =>
        s.type === 'section' &&
        s.title === sectionName &&
        hasPermission(s.allowedProfiles)
      );
      if (sectionData) {
        filtered.push({
          ...sectionData,
          items: items
        });
      }
    });

    setFilteredSections(filtered);

    // Abrir todas as seções quando há busca
    if (filtered.length > 0) {
      setOpenManagementSubmenu(true);
      setOpenCommunicationSubmenu(true);
      setOpenCampaignSubmenu(true);
      setOpenFlowSubmenu(true);
      setOpenAdministrationSubmenu(true);
      setOpenIntegrationSubmenu(true);
    }
  }, [searchQuery]);

  useEffect(() => {
    async function checkHelps() {
      const helps = await list();
      setHasHelps(helps.length > 0);
    }
    checkHelps();
  }, []);

  useEffect(() => {
    async function fetchVersion() {
      try {
        const versionData = await getVersion();
        if (versionData && versionData.version) {
          setVersion(versionData.version);
        }
      } catch (err) {
        console.error("Erro ao buscar versão:", err);
      }
    }
    fetchVersion();
  }, []);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    async function fetchData() {
      try {
        const companyId = user.companyId;
        const planConfigs = await getPlanCompany(undefined, companyId);

        setShowCampaigns(planConfigs.plan.useCampaigns);
        setShowKanban(planConfigs.plan.useKanban);
        setShowOpenAi(planConfigs.plan.useOpenAi);
        setShowIntegrations(planConfigs.plan.useIntegrations);
        setShowSchedules(planConfigs.plan.useSchedules);
        setShowInternalChat(planConfigs.plan.useInternalChat);
        setShowExternalApi(planConfigs.plan.useExternalApi);
        setPlanExpired(moment(moment().format()).isBefore(user.company.dueDate));
      } catch (err) {
        console.error("Erro ao buscar dados do plano:", err);
      }
    }
    fetchData();
  }, []);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchChats();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber]);

  useEffect(() => {
    if (user.id) {
      const companyId = user.companyId;
      const onCompanyChatMainListItems = (data) => {
        if (data.action === "new-message") {
          dispatch({ type: "CHANGE_CHAT", payload: data });
        }
        if (data.action === "update") {
          dispatch({ type: "CHANGE_CHAT", payload: data });
        }
      };

      socket.on(`company-${companyId}-chat`, onCompanyChatMainListItems);
      return () => {
        socket.off(`company-${companyId}-chat`, onCompanyChatMainListItems);
      };
    }
  }, [socket]);

  useEffect(() => {
    let unreadsCount = 0;
    if (chats.length > 0) {
      for (let chat of chats) {
        for (let chatUser of chat.users) {
          if (chatUser.userId === user.id) {
            unreadsCount += chatUser.unreads;
          }
        }
      }
    }
    setUnreadCount(unreadsCount);
    setInvisible(unreadsCount === 0);
  }, [chats, user.id]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (whatsApps.length > 0) {
        const offlineWhats = whatsApps.filter((whats) => {
          return (
            whats.status === "qrcode" ||
            whats.status === "PAIRING" ||
            whats.status === "DISCONNECTED" ||
            whats.status === "TIMEOUT" ||
            whats.status === "OPENING"
          );
        });
        setConnectionWarning(offlineWhats.length > 0);
      }
    }, 2000);
    return () => clearTimeout(delayDebounceFn);
  }, [whatsApps]);

  const fetchChats = async () => {
    try {
      const { data } = await api.get("/chats/", {
        params: { searchParam, pageNumber },
      });
      dispatch({ type: "LOAD_CHATS", payload: data.records });
    } catch (err) {
      toastError(err);
    }
  };

  const getUserInitials = () => {
    if (user && user.name) {
      const names = user.name.split(' ');
      return names.length > 1
        ? `${names[0][0]}${names[names.length - 1][0]}`.toUpperCase()
        : names[0][0].toUpperCase();
    }
    return "U";
  };

  const handleUserMenuOpen = (event) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  const handleLogoutClick = () => {
    handleUserMenuClose();
    handleLogout();
  };

  const handleClearSearch = () => {
    setSearchQuery("");
  };

  const SubMenuSection = ({ title, icon, isOpen, onToggle, children, forceOpen = false, allowedProfiles = [] }) => {
    // Verificar se o usuário tem permissão para ver esta seção
    if (!hasPermission(allowedProfiles)) {
      return null;
    }

    const shouldBeOpen = forceOpen || isOpen;
    return (
      <>
        <ListItem
          button
          onClick={onToggle}
          className={classes.menuItem}
        >
          <ListItemIcon className={classes.menuItemIcon}>
            <Box className="menu-item-icon">{icon ? React.createElement(icon) : <DashboardIcon />}</Box>
          </ListItemIcon>
          {!collapsed && (
            <>
              <ListItemText
                primary={
                  <Typography className={classes.menuItemText}>
                    {title}
                  </Typography>
                }
              />
              {shouldBeOpen ? (
                <ExpandLessIcon className={classes.expandIcon} />
              ) : (
                <ExpandMoreIcon className={classes.expandIcon} />
              )}
            </>
          )}
        </ListItem>
        <Collapse in={shouldBeOpen} timeout="auto" unmountOnExit>
          {children}
        </Collapse>
      </>
    );
  };

  // Renderizar menu normal (sem busca)
  const renderNormalMenu = () => (
    <>
      {/* Seção de Comunicação */}
      {hasPermission(['admin', 'supervisor', 'user']) && (
        <Box className={classes.menuSection}>
          {!collapsed && (
            <Typography className={classes.sectionTitle}>
              Comunicação
            </Typography>
          )}
          <SubMenuSection
            title="Comunicação"
            icon={ChatBubbleIcon}
            isOpen={openCommunicationSubmenu}
            onToggle={() => setOpenCommunicationSubmenu(!openCommunicationSubmenu)}
            allowedProfiles={['admin', 'supervisor', 'user']}
          >
            {planExpired && (
              <MenuItemLink
                to="/tickets"
                primary={i18n.t("mainDrawer.listItems.tickets")}
                icon={<WhatsAppIcon />}
                badge={unreadCount > 0 ? unreadCount : null}
                collapsed={collapsed}
                isSubItem={true}
              />
            )}

            {planExpired && (
              <MenuItemLink
                to="/quick-messages"
                primary={i18n.t("mainDrawer.listItems.quickMessages")}
                icon={<FlashOnIcon />}
                collapsed={collapsed}
                isSubItem={true}
              />
            )}

            {planExpired && (
              <MenuItemLink
                to="/contacts"
                primary={i18n.t("mainDrawer.listItems.contacts")}
                icon={<ContactPhoneIcon />}
                collapsed={collapsed}
                isSubItem={true}
              />
            )}

            {showSchedules && planExpired && (
              <MenuItemLink
                to="/schedules"
                primary={i18n.t("mainDrawer.listItems.schedules")}
                icon={<ScheduleIcon />}
                collapsed={collapsed}
                isSubItem={true}
              />
            )}

            {planExpired && (
              <MenuItemLink
                to="/tags"
                primary={i18n.t("mainDrawer.listItems.tags")}
                icon={<LocalOfferIcon />}
                collapsed={collapsed}
                isSubItem={true}
              />
            )}

            {showInternalChat && planExpired && (
              <MenuItemLink
                to="/chats"
                primary={i18n.t("mainDrawer.listItems.chats")}
                icon={
                  <Badge color="secondary" variant="dot" invisible={invisible}>
                    <ForumIcon />
                  </Badge>
                }
                collapsed={collapsed}
                isSubItem={true}
              />
            )}
          </SubMenuSection>
        </Box>
      )}

      {/* Seção de Gerenciamento */}
      {hasPermission(['admin', 'supervisor']) && planExpired && (
        <Can
          role={
            (user.profile === "user" && user.showDashboard === "enabled") || user.allowRealTime === "enabled"
              ? "admin"
              : user.profile
          }
          perform={"drawer-admin-items:view"}
          yes={() => (
            <Box className={classes.menuSection}>
              {!collapsed && (
                <Typography className={classes.sectionTitle}>
                  {i18n.t("mainDrawer.listItems.management")}
                </Typography>
              )}
              <SubMenuSection
                title={i18n.t("mainDrawer.listItems.management")}
                icon={DashboardIcon}
                isOpen={openManagementSubmenu}
                onToggle={() => setOpenManagementSubmenu(!openManagementSubmenu)}
                allowedProfiles={['admin', 'supervisor']}
              >
                <Can
                  role={user.profile === "user" && user.showDashboard === "enabled" ? "admin" : user.profile}
                  perform={"drawer-admin-items:view"}
                  yes={() => (
                    <>
                      <MenuItemLink
                        to="/"
                        primary="Dashboard"
                        icon={<AssessmentIcon />}
                        collapsed={collapsed}
                        isSubItem={true}
                      />
                      <MenuItemLink
                        to="/reports-central"
                        primary="Central de Relatórios"
                        icon={<EqualizerIcon />}
                        collapsed={collapsed}
                        isSubItem={true}
                      />
                      {/* <MenuItemLink
                        to="/reports"
                        primary={i18n.t("mainDrawer.listItems.reports")}
                        icon={<ListAltIcon />}
                        collapsed={collapsed}
                        isSubItem={true}
                      />
                      <MenuItemLink
                        to="/tags-report"
                        primary={"Relatório de Tags"}
                        icon={<LocalOfferIcon />}
                        collapsed={collapsed}
                        isSubItem={true}
                      />
                      <MenuItemLink
                        to="/pauses/report"
                        primary="Relatório de Pausas"
                        icon={<ListAltIcon />}
                        collapsed={collapsed}
                        isSubItem={true}
                      />
                      <MenuItemLink
                        to="/users-report"
                        primary="Relatório de Usuários"
                        icon={<PeopleIcon />}
                        collapsed={collapsed}
                        isSubItem={true}
                      />
                      <MenuItemLink
                        to="/ratings-report"
                        primary="Relatório de Avaliações"
                        icon={<StarIcon />}
                        collapsed={collapsed}
                        isSubItem={true}
                      /> */}
                    </>
                  )}
                />
                <Can
                  role={user.profile === "user" && user.allowRealTime === "enabled" ? "admin" : user.profile}
                  perform={"drawer-admin-items:view"}
                  yes={() => (
                    <MenuItemLink
                      to="/moments"
                      primary={i18n.t("mainDrawer.listItems.chatsTempoReal")}
                      icon={<ForumIcon />}
                      collapsed={collapsed}
                      isSubItem={true}
                    />
                  )}
                />
              </SubMenuSection>
            </Box>
          )}
        />
      )}

      {/* Seção de Campanhas */}
      {hasPermission(['admin', 'supervisor']) && showCampaigns && planExpired && (
        <Can
          role={user.profile}
          perform="dashboard:view"
          yes={() => (
            <Box className={classes.menuSection}>
              {!collapsed && (
                <Typography className={classes.sectionTitle}>
                  {i18n.t("mainDrawer.listItems.campaigns")}
                </Typography>
              )}
              <SubMenuSection
                title={i18n.t("mainDrawer.listItems.campaigns")}
                icon={CampaignIcon}
                isOpen={openCampaignSubmenu}
                onToggle={() => setOpenCampaignSubmenu(!openCampaignSubmenu)}
                allowedProfiles={['admin', 'supervisor']}
              >
                <MenuItemLink
                  to="/campaign-wizard"
                  primary="Envio Rápido"
                  icon={<FlashOnIcon />}
                  collapsed={collapsed}
                  isSubItem={true}
                />
                <MenuItemLink
                  to="/contact-lists"
                  primary={i18n.t("campaigns.subMenus.listContacts")}
                  icon={<PeopleIcon />}
                  collapsed={collapsed}
                  isSubItem={true}
                />
                <MenuItemLink
                  to="/campaigns"
                  primary={i18n.t("mainDrawer.listItems.campaigns")}
                  icon={<CampaignIcon />}
                  collapsed={collapsed}
                  isSubItem={true}
                />
                <MenuItemLink
                  to="/campaigns-config"
                  primary={i18n.t("campaigns.subMenus.settings")}
                  icon={<SettingsIcon />}
                  collapsed={collapsed}
                  isSubItem={true}
                />
              </SubMenuSection>
            </Box>
          )}
        />
      )}

      {/* Seção de Fluxos */}
      {hasPermission(['admin', 'supervisor']) && planExpired && (
        <Can
          role={user.profile}
          perform="dashboard:view"
          yes={() => (
            <Box className={classes.menuSection}>
              {!collapsed && (
                <Typography className={classes.sectionTitle}>
                  Fluxos
                </Typography>
              )}
              <SubMenuSection
                title="Fluxos"
                icon={WebhookIcon}
                isOpen={openFlowSubmenu}
                onToggle={() => setOpenFlowSubmenu(!openFlowSubmenu)}
                allowedProfiles={['admin', 'supervisor']}
              >
                <MenuItemLink
                  to="/phrase-lists"
                  primary="Fluxo de Campanha"
                  icon={<CampaignIcon />}
                  collapsed={collapsed}
                  isSubItem={true}
                />
                <MenuItemLink
                  to="/flowbuilders"
                  primary="Fluxo de conversa"
                  icon={<ShapeLineIcon />}
                  collapsed={collapsed}
                  isSubItem={true}
                />
              </SubMenuSection>
            </Box>
          )}
        />
      )}

      {/* Seção de Ferramentas */}
      {hasPermission(['admin', 'supervisor', 'user']) && (
        <Box className={classes.menuSection}>
          {!collapsed && (
            <Typography className={classes.sectionTitle}>
              Ferramentas
            </Typography>
          )}
          <SubMenuSection
            title="Ferramentas"
            icon={BuildIcon}
            isOpen={openIntegrationSubmenu}
            onToggle={() => setOpenIntegrationSubmenu(!openIntegrationSubmenu)}
            allowedProfiles={['admin', 'supervisor', 'user']}
          >
            {showKanban && planExpired && (
              <MenuItemLink
                to="/kanban"
                primary={i18n.t("mainDrawer.listItems.kanban")}
                icon={<ViewKanbanIcon />}
                collapsed={collapsed}
                isSubItem={true}
              />
            )}

            <MenuItemLink
              to="/todolist"
              primary={i18n.t("Tarefas")}
              icon={<EventAvailableIcon />}
              collapsed={collapsed}
              isSubItem={true}
            />

            <MenuItemLink
              to="/helps"
              primary={i18n.t("mainDrawer.listItems.helps")}
              icon={<HelpOutlineIcon />}
              collapsed={collapsed}
              isSubItem={true}
            />

            <MenuItemLink
              to="/documentacao"
              primary={i18n.t("Documentação")}
              icon={<CodeIcon />}
              collapsed={collapsed}
              isSubItem={true}
            />
          </SubMenuSection>
        </Box>
      )}

      {/* Seção de Administração */}
      {hasPermission(['admin']) && (
        <Can
          role={user.profile === "user" && user.allowConnections === "enabled" ? "admin" : user.profile}
          perform="dashboard:view"
          yes={() => (
            <Box className={classes.menuSection}>
              {!collapsed && (
                <Typography className={classes.sectionTitle}>
                  Administração
                </Typography>
              )}
              <SubMenuSection
                title="Administração"
                icon={SettingsIcon}
                isOpen={openAdministrationSubmenu}
                onToggle={() => setOpenAdministrationSubmenu(!openAdministrationSubmenu)}
                allowedProfiles={['admin']}
              >
                {user.super && (
                  <MenuItemLink
                    to="/announcements"
                    primary={i18n.t("mainDrawer.listItems.annoucements")}
                    icon={<AnnouncementIcon />}
                    collapsed={collapsed}
                    isSubItem={true}
                  />
                )}

                {showExternalApi && planExpired && (
                  <Can
                    role={user.profile}
                    perform="dashboard:view"
                    yes={() => (
                      <MenuItemLink
                        to="/messages-api"
                        primary={i18n.t("mainDrawer.listItems.messagesAPI")}
                        icon={<CodeIcon />}
                        collapsed={collapsed}
                        isSubItem={true}
                      />
                    )}
                  />
                )}

                {planExpired && (
                  <Can
                    role={user.profile}
                    perform="dashboard:view"
                    yes={() => (
                      <MenuItemLink
                        to="/users"
                        primary={i18n.t("mainDrawer.listItems.users")}
                        icon={<PeopleIcon />}
                        collapsed={collapsed}
                        isSubItem={true}
                      />
                    )}
                  />
                )}

                {planExpired && (
                  <Can
                    role={user.profile}
                    perform="dashboard:view"
                    yes={() => (
                      <MenuItemLink
                        to="/queues"
                        primary={i18n.t("mainDrawer.listItems.queues")}
                        icon={<AccountTreeIcon />}
                        collapsed={collapsed}
                        isSubItem={true}
                      />
                    )}
                  />
                )}

                {planExpired && (
                  <Can
                    role={user.profile}
                    perform="dashboard:view"
                    yes={() => (
                      <MenuItemLink
                        to="/pauses"
                        primary="Pausas"
                        icon={<SettingsIcon />}
                        collapsed={collapsed}
                        isSubItem={true}
                      />
                    )}
                  />
                )}

                {showOpenAi && planExpired && (
                  <Can
                    role={user.profile}
                    perform="dashboard:view"
                    yes={() => (
                      <MenuItemLink
                        to="/prompts"
                        primary={i18n.t("mainDrawer.listItems.prompts")}
                        icon={<AllInclusiveIcon />}
                        collapsed={collapsed}
                        isSubItem={true}
                      />
                    )}
                  />
                )}

                {showIntegrations && planExpired && (
                  <Can
                    role={user.profile}
                    perform="dashboard:view"
                    yes={() => (
                      <MenuItemLink
                        to="/queue-integration"
                        primary={i18n.t("mainDrawer.listItems.queueIntegration")}
                        icon={<DeviceHubIcon />}
                        collapsed={collapsed}
                        isSubItem={true}
                      />
                    )}
                  />
                )}

                {planExpired && (
                  <Can
                    role={user.profile === "user" && user.allowConnections === "enabled" ? "admin" : user.profile}
                    perform={"drawer-admin-items:view"}
                    yes={() => (
                      <MenuItemLink
                        to="/connections"
                        primary={i18n.t("mainDrawer.listItems.connections")}
                        icon={<SyncAltIcon />}
                        badge={connectionWarning ? "!" : null}
                        collapsed={collapsed}
                        isSubItem={true}
                      />
                    )}
                  />
                )}

                {user.super && (
                  <MenuItemLink
                    to="/allConnections"
                    primary={i18n.t("mainDrawer.listItems.allConnections")}
                    icon={<SyncAltIcon />}
                    collapsed={collapsed}
                    isSubItem={true}
                  />
                )}

                {planExpired && (
                  <Can
                    role={user.profile}
                    perform="dashboard:view"
                    yes={() => (
                      <MenuItemLink
                        to="/files"
                        primary={i18n.t("mainDrawer.listItems.files")}
                        icon={<AttachFileIcon />}
                        collapsed={collapsed}
                        isSubItem={true}
                      />
                    )}
                  />
                )}

                <Can
                  role={user.profile}
                  perform="dashboard:view"
                  yes={() => (
                    <MenuItemLink
                      to="/financeiro"
                      primary={i18n.t("mainDrawer.listItems.financeiro")}
                      icon={<LocalAtmIcon />}
                      collapsed={collapsed}
                      isSubItem={true}
                    />
                  )}
                />

                {planExpired && (
                  <Can
                    role={user.profile}
                    perform="dashboard:view"
                    yes={() => (
                      <MenuItemLink
                        to="/settings"
                        primary={i18n.t("mainDrawer.listItems.settings")}
                        icon={<SettingsIcon />}
                        collapsed={collapsed}
                        isSubItem={true}
                      />
                    )}
                  />
                )}

                {user.super && (
                  <MenuItemLink
                    to="/companies"
                    primary={i18n.t("mainDrawer.listItems.companies")}
                    icon={<BusinessIcon />}
                    collapsed={collapsed}
                    isSubItem={true}
                  />
                )}
              </SubMenuSection>
            </Box>
          )}
        />
      )}
    </>
  );

  // Renderizar menu filtrado (com busca)
  const renderFilteredMenu = () => {
    if (filteredSections.length === 0 && searchQuery.trim()) {
      return (
        <Box className={classes.noResults}>
          Nenhum resultado encontrado para "{searchQuery}"
        </Box>
      );
    }

    return filteredSections.map((section, index) => (
      <Box key={index} className={classes.menuSection}>
        {!collapsed && (
          <Typography className={classes.sectionTitle}>
            {section.title}
          </Typography>
        )}
        <SubMenuSection
          title={section.title}
          icon={section.icon}
          isOpen={true}
          forceOpen={true}
          onToggle={() => { }}
          allowedProfiles={section.allowedProfiles}
        >
          {section.items.map((item, itemIndex) => (
            <MenuItemLink
              key={itemIndex}
              to={item.to}
              primary={item.title}
              icon={React.createElement(item.icon)}
              collapsed={collapsed}
              isSubItem={true}
              highlight={true}
            />
          ))}
        </SubMenuSection>
      </Box>
    ));
  };

  return (
    <Box className={`${classes.menuWrapper} ${collapsed ? classes.collapsedMenu : ""}`} onClick={drawerClose}>
      {/* Cabeçalho do Menu */}
      <Box className={classes.menuHeader}>
        <Box
          className={classes.userInfo}
          onClick={handleUserMenuOpen}
        >
          <Avatar className={classes.userAvatar}>
            {getUserInitials()}
          </Avatar>
          {!collapsed && (
            <Box className={classes.userDetails}>
              <Typography className={classes.userName}>
                {user?.name || "Usuário"}
              </Typography>
              <Typography className={classes.userRole}>
                {user?.profile === "admin" ? "Administrador" :
                  user?.profile === "supervisor" ? "Supervisor" : "Usuário"}
              </Typography>
            </Box>
          )}
        </Box>

        {!collapsed && (
          <Box className={classes.searchBar}>
            <SearchIcon className={classes.searchIcon} />
            <input
              type="text"
              placeholder="Buscar no menu..."
              className={classes.searchInput}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <ClearIcon
                className={classes.clearIcon}
                onClick={handleClearSearch}
              />
            )}
          </Box>
        )}
      </Box>

      {/* Menu de usuário (dropdown) */}
      <Menu
        anchorEl={userMenuAnchor}
        keepMounted
        open={Boolean(userMenuAnchor)}
        onClose={handleUserMenuClose}
        className={classes.userMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <MenuItem onClick={() => { /* Abrir modal de perfil */ handleUserMenuClose(); }}>
          <PersonIcon style={{ fontSize: 18, marginRight: 8, color: "#6517ab" }} />
          Meu Perfil
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogoutClick}>
          <ExitToAppIcon style={{ fontSize: 18, marginRight: 8, color: "#ff4757" }} />
          Sair
        </MenuItem>
      </Menu>

      {/* Conteúdo do Menu */}
      <Box className={classes.menuContent}>
        {searchQuery.trim() ? renderFilteredMenu() : renderNormalMenu()}
      </Box>

      {/* Rodapé do Menu - APENAS SAIR */}
      <Box className={classes.menuFooter}>
        {/* Botão Sair no rodapé */}
        <Box
          className={classes.footerItem}
          onClick={handleLogoutClick}
        >
          <ExitToAppIcon className={classes.footerItemIcon} />
          {!collapsed && (
            <Typography className={classes.footerItemText}>
              Sair
            </Typography>
          )}
        </Box>

        {/* Versão do sistema */}
        {!collapsed && version && (
          <Typography className={classes.versionText}>
            v{version}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default MainListItems;